import { zodResolver } from "@hookform/resolvers/zod";
import {
  useGetCareprovider,
  useGetCareseeker,
  useGetCareseekerAccounts,
  useGetReceiverAccounts,
} from "apollo/hooks/queries";
import { Result, composeProgress } from "apollo/utils";
import { capitaliseFirst } from "core/model/utils/strings";
import { Account, QueryProgress } from "core/types";
import { ERROR_COLOR, SUCCESS_COLOR } from "ds_legacy/materials/colors";
import { padding } from "ds_legacy/materials/metrics";
import {
  Caption,
  FONT_SIZE_16,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import { Button, ControlledInput, Form } from "ds/ui";
import { useAcpNavigationHandlers } from "dsl/hooks";
import { CheckIcon, XCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useTranslations } from "translations";
import { AccountsTable } from "../Accounts";
import { SealdDebugSearchFormValues, sealdDebugSearchSchema } from "./schema";
import { DetailsTable, DetailsTableRow } from "./shared";

export default function GroupPage({ page }: { page: "receiver" | "sender" }) {
  const translations = useTranslations();
  const navigationHandlers = useAcpNavigationHandlers();
  const [search, setSearch] = useSearchParams();
  const [groupId, setGroupId] = useState<number>();

  const [careseekerQueryProgress, careseeker] = useGetCareseeker(
    groupId ?? undefined,
    {},
    { skip: page !== "sender" || !groupId },
  );
  const [careproviderQueryProgress, careprovider] = useGetCareprovider({
    id: groupId ?? -1,
    options: { skip: page !== "receiver" || !groupId },
    queryOptions: { withRoles: true },
  });

  const queryParamName =
    page === "sender" ? "careseeker_id" : "careprovider_id";
  const groupIdQueryparam = search.get(queryParamName);

  const methods = useForm<SealdDebugSearchFormValues>({
    defaultValues: { entity_id: groupIdQueryparam ?? "" },
    resolver: zodResolver(sealdDebugSearchSchema),
  });

  useEffect(() => {
    if (groupIdQueryparam) {
      setGroupId(Number(groupIdQueryparam));
    } else {
      setGroupId(undefined);
    }
    methods.reset({ entity_id: groupIdQueryparam ?? "" });
  }, [groupIdQueryparam, methods]);

  const onSubmit = methods.handleSubmit(({ entity_id }) => {
    setGroupId(Number(entity_id));
    search.set(queryParamName, entity_id);
    setSearch(search);
  });

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={onSubmit}>
          <div className="flex flex-row items-end gap-2">
            <ControlledInput
              label={page === "sender" ? "Sender ID" : "Receiver ID"}
              name="entity_id"
            />
            <Button id="search" type="submit" variant="ghost">
              Search
            </Button>
          </div>
        </Form>
      </FormProvider>
      <GetAccountsQueryPerPage groupId={groupId} page={page}>
        {(accounts, queryProgress) => (
          <Result
            data={{}}
            ErrorComponent={
              <Caption>
                Could not get {page} {groupId}
              </Caption>
            }
            id="careprovider"
            queryProgress={composeProgress([
              queryProgress,
              careseekerQueryProgress,
              careproviderQueryProgress,
            ])}
          >
            {() => (
              <>
                <Title>
                  {capitaliseFirst(page)} {groupId} -{" "}
                  {page === "sender" ? careseeker?.name : careprovider?.name}
                </Title>
                <DetailsTable>
                  <tbody>
                    <DetailsTableRow>
                      <td
                        style={{
                          padding: padding(0),
                        }}
                      >
                        <Subheading>encryption activated for {page}</Subheading>
                      </td>
                      <td
                        style={{
                          padding: padding(0, 4),
                        }}
                      >
                        {(page === "sender" &&
                          careseeker?.seald_encryption_context) ||
                        (page === "receiver" &&
                          careprovider?.seald_encryption_context) ? (
                          <CheckIcon
                            data-testid="success-icon"
                            size={FONT_SIZE_16}
                            style={{ color: SUCCESS_COLOR }}
                          />
                        ) : (
                          <XCircleIcon
                            data-testid="cancel-icon"
                            size={FONT_SIZE_16}
                            style={{ color: ERROR_COLOR }}
                          />
                        )}
                      </td>
                    </DetailsTableRow>
                  </tbody>
                </DetailsTable>
                <AccountsTable
                  accounts={accounts ?? []}
                  careproviderId={page === "receiver" ? groupId : undefined}
                  careseekerId={page === "sender" ? groupId : undefined}
                  goToAccount={(accountId) =>
                    navigationHandlers.goToSealdDebug(
                      `account?account_id=${accountId}`,
                    )
                  }
                  pageState={{}}
                  sealdId={undefined}
                  setPageState={() => {}}
                  title={translations.careproviderApp.settings.accounts.title}
                  withAddAccount={false}
                />
              </>
            )}
          </Result>
        )}
      </GetAccountsQueryPerPage>
    </>
  );
}

function GetAccountsQueryPerPage({
  children,
  groupId,
  page,
}: {
  children: (
    accounts: Readonly<Account[]> | undefined,
    queryProgress: QueryProgress,
  ) => React.ReactNode;
  groupId: number | undefined;
  page: "sender" | "receiver";
}) {
  const [senderQueryProgress, senderAccounts] = useGetCareseekerAccounts({
    careseekerId: groupId,
    fetchPolicy: "network-only",
    skip: page === "receiver",
  });

  const [receiverQueryProgress, receiverAccounts] = useGetReceiverAccounts({
    careproviderId: groupId,
    fetchPolicy: "network-only",
    skip: page === "sender",
  });

  if (!groupId) return null;

  if (page === "sender") {
    return <>{children(senderAccounts, senderQueryProgress)}</>;
  }

  return <>{children(receiverAccounts, receiverQueryProgress)}</>;
}
